import React, { useCallback, useEffect, useState } from 'react';
import styles from '../../styles/ReporteCargaAcademicaStyles/ReporteCargaAcademica.module.css';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import LoaderOverlay from '../../components/LoaderOverlay';
import FiltrosCargaAcademica from './FiltrosCargaAcademica';
import TablaCargaAcademica from './TablaCargaAcademica';
import { getReporteCargaAcademica } from '../../api/reporteCargaAcademica';
import { exportReporteCargaAcademicaXLS } from '../../utils/exportReporteCargaAcademicaXLS';
import { getCampus } from '../../api/campus';

function ReporteCargaAcademicaScreen() {
  const { isCollapsed } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [contexto, setContexto] = useState({});
  const [campusList, setCampusList] = useState([]);

  const [filters, setFilters] = useState({
    tipo_reporte: 'ASESOR',
    id_ciclo_escolar: '',
    id_campus: '',
    id_seccion: '',
    id_asesor: '',
    id_estudiante: '',
    matricula: '',
  });

  // Cargar lista de campus una sola vez (para resolver nombre→ID y contexto)
  useEffect(() => {
    let mounted = true;
    getCampus().catch(() => []).then(cam => {
      if (mounted) setCampusList(Array.isArray(cam) ? cam : []);
    });
    return () => { mounted = false; };
  }, []);

  // Sincronizar id_campus con el header global
  useEffect(() => {
    if (!campusList.length) return;
    const resolve = (name) => {
      const found = campusList.find(c => c.nombre === name || String(c.id) === name);
      setFilters(prev => ({ ...prev, id_campus: found ? String(found.id) : '' }));
    };
    const handler = (e) => resolve((e.detail?.campus) || localStorage.getItem('selectedCampus') || '');
    window.addEventListener('campusChanged', handler);
    resolve(localStorage.getItem('selectedCampus') || '');
    return () => window.removeEventListener('campusChanged', handler);
  }, [campusList]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Enriquecer el contexto con campus desde el header global
  const handleContextResolved = useCallback((ctx) => {
    const campusName = localStorage.getItem('selectedCampus') || '';
    const campusObj = campusList.find(c => c.nombre === campusName || String(c.id) === campusName);
    setContexto({ ...ctx, campus: campusObj ? campusObj.nombre : (campusName || 'Todos') });
  }, [campusList]);

  const handleGenerate = useCallback(async () => {
    if (!filters.tipo_reporte) {
      setError('Selecciona un tipo de reporte.');
      return;
    }
    if (!filters.id_ciclo_escolar) {
      setError('Selecciona un ciclo escolar para continuar.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getReporteCargaAcademica({
        tipo_reporte: filters.tipo_reporte,
        id_ciclo_escolar: filters.id_ciclo_escolar,
        id_campus: filters.id_campus,
        id_seccion: filters.id_seccion,
        id_asesor: filters.tipo_reporte === 'ASESOR' ? filters.id_asesor : undefined,
        id_estudiante: filters.tipo_reporte === 'ESTUDIANTE' ? filters.id_estudiante : undefined,
        // Si el usuario tipeó matrícula sin elegir sugerencia, igual la mandamos
        matricula: filters.tipo_reporte === 'ESTUDIANTE' && !filters.id_estudiante
          ? filters.matricula
          : undefined,
      });
      setReportData(data);
    } catch (err) {
      console.error('[ReporteCargaAcademicaScreen] Error:', err);
      setError(err.message || 'No se pudo obtener el reporte.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleExportXLS = useCallback(() => {
    const resultados = reportData?.resultados || [];
    if (!Array.isArray(resultados) || resultados.length === 0) {
      return;
    }

    exportReporteCargaAcademicaXLS({
      resultados,
      tipoReporte: reportData?.tipo_reporte || filters.tipo_reporte,
      contexto,
    });
  }, [reportData, filters.tipo_reporte, contexto]);

  const hasResults = Array.isArray(reportData?.resultados) && reportData.resultados.length > 0;

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <main className={styles.mainContent}>
            <HeaderGlobal title="Reporte de Carga Académica" />
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Generando reporte..." />

                <FiltrosCargaAcademica
                  filters={filters}
                  onChange={handleFilterChange}
                  onGenerate={handleGenerate}
                  loading={loading}
                  onContextResolved={handleContextResolved}
                />

                {error && (
                  <div
                    style={{
                      background: '#fdecea',
                      color: '#c5221f',
                      padding: '10px 14px',
                      borderRadius: 4,
                      marginBottom: 16,
                      fontSize: 13,
                    }}
                  >
                    {error}
                  </div>
                )}

                {reportData ? (
                  <div className={styles.reportContainer}>
                    <TablaCargaAcademica
                      resultados={reportData.resultados || []}
                      tipoReporte={reportData.tipo_reporte || filters.tipo_reporte}
                      contexto={contexto}
                    />
                    <div className={styles.actionsRow}>
                      <button
                        type="button"
                        className={styles.xlsButton}
                        onClick={handleExportXLS}
                        disabled={!hasResults}
                        title={hasResults ? 'Exportar a XLSX' : 'No hay datos para exportar'}
                      >
                        <span role="img" aria-label="excel">📊</span>
                        Exportar Excel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.reportContainer}>
                    <div className={styles.emptyState}>
                      <div className={styles.emptyIcon}>📚</div>
                      <p className={styles.emptyText}>Sin reporte generado</p>
                      <p className={styles.emptySubText}>
                        Selecciona los filtros y presiona <strong>Generar Reporte</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default ReporteCargaAcademicaScreen;
